import React, { useEffect, useMemo, useState } from 'react';
import { Button, Col, Form, Input, Row, Select, Space, Typography, Tag, Modal, Divider, Radio, Checkbox } from 'antd';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { SearchOutlined } from '@ant-design/icons';
import { TableCustom } from '../../components/TableCustom';
import { formatDateVN, newObjectKeys } from '../../utils/utility';
import { getQuizAttempts } from '../../services/quizAttempt';
import { getAllLessons } from '../../services/lesson';
import { getCurriculums } from '../../services/curriculum';
import { getAllCategories } from '../../services/category';
import { getUsers } from '../../services/user';
import { getQuizzes, getQuizInfo } from '../../services/quiz';
import dayjs from 'dayjs';

const { Text } = Typography;
const itemName = 'Nhật ký học bài';

const QuizAttemptsList = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [formSearch] = Form.useForm();
  const [params, setParams] = useState({ page: 1, limit: 20 });
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailRecord, setDetailRecord] = useState(null);

  // quiz detail for modal (load when opened)
  const quizIdForDetail = detailRecord?.quiz_id;
  const { data: quizDetail } = useQuery({
    queryKey: ['quizDetailForAttempt', quizIdForDetail],
    queryFn: () => getQuizInfo(quizIdForDetail),
    enabled: !!quizIdForDetail && detailOpen,
    staleTime: 1000 * 60 * 5,
  });

  // init from URL
  useEffect(() => {
    const page = Number(searchParams.get('page')) || 1;
    const limit = Number(searchParams.get('limit')) || 20;
    const userStr = searchParams.get('user_id');
    const userVal = userStr !== null ? Number(userStr) : -1;
    const lessonStr = searchParams.get('lesson_id');
    const lessonVal = lessonStr !== null ? Number(lessonStr) : -1;

    const next = { page, limit };
    if (userVal > 0) Object.assign(next, { user_id: userVal });
    if (lessonVal > 0) Object.assign(next, { lesson_id: lessonVal });

    setParams(next);
    formSearch.setFieldsValue({ user_id: userVal > 0 ? userVal : -1, lesson_id: lessonVal > 0 ? lessonVal : -1 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // sync URL
  useEffect(() => {
    if (!params) return;
    const sp = { page: String(params.page || 1), limit: String(params.limit || 20) };
    if (typeof params.user_id === 'number' && params.user_id > 0) sp.user_id = String(params.user_id);
    if (typeof params.lesson_id === 'number' && params.lesson_id > 0) sp.lesson_id = String(params.lesson_id);
    setSearchParams(sp, { replace: true });
  }, [params, setSearchParams]);

  // data
  const { data: dataAttempts, isPending, refetch } = useQuery({ queryKey: ['quizAttempts', params], queryFn: () => getQuizAttempts(params) });

  // ref data for rendering/filters
  const { data: lessonListRes } = useQuery({ queryKey: ['allLessonsForAttempts'], queryFn: getAllLessons, staleTime: 1000 * 60 * 10 });
  const { data: curriculumListRes } = useQuery({ queryKey: ['allCurriculumsForAttempts'], queryFn: () => getCurriculums({ page: 1, limit: 10000, status: [1] }), staleTime: 1000 * 60 * 10 });
  const { data: allCategoriesRes } = useQuery({ queryKey: ['allCategoriesForAttempts'], queryFn: getAllCategories, staleTime: 1000 * 60 * 10 });
  const { data: usersRes } = useQuery({ queryKey: ['allUsersForAttempts'], queryFn: () => getUsers({ page: 1, limit: 10000 }), staleTime: 1000 * 60 * 10 });
  const { data: quizzesRes } = useQuery({ queryKey: ['allQuizzesForAttempts'], queryFn: () => getQuizzes({ page: 1, limit: 10000, status: [1] }), staleTime: 1000 * 60 * 10 });

  const lessonPathLabelMap = useMemo(() => {
    const lessons = Array.isArray(lessonListRes?.data) ? lessonListRes.data : [];
    const currs = Array.isArray(curriculumListRes?.data) ? curriculumListRes.data : [];
    const cats = Array.isArray(allCategoriesRes?.data) ? allCategoriesRes.data : [];
    const curTitle = currs.reduce((acc, c) => { acc[c.id] = c.title || ''; return acc; }, {});
    const curCat = currs.reduce((acc, c) => { acc[c.id] = c.category_id || 0; return acc; }, {});
    const parentMap = cats.reduce((acc, c) => { acc[c.id] = c.parent_id || 0; return acc; }, {});
    const titleMap = cats.reduce((acc, c) => { acc[c.id] = c.title || ''; return acc; }, {});
    const cache = {};
    const toCatPath = (id) => {
      if (cache[id]) return cache[id];
      const parts = [];
      let cur = id; const guard = new Set();
      while (cur && !guard.has(cur) && parts.length < 10) { guard.add(cur); parts.unshift(titleMap[cur] || ''); cur = parentMap[cur] || 0; }
      cache[id] = parts.filter(Boolean).join(' — ');
      return cache[id];
    };
    const map = {};
    lessons.forEach((l) => {
      const catId = curCat[l.curriculum_id] || 0;
      const catPath = toCatPath(catId);
      const pTitle = curTitle[l.curriculum_id] || '';
      const lTitle = l.title || '';
      map[l.id] = `${catPath || '—'} - ${pTitle} - ${lTitle}`;
    });
    return map;
  }, [lessonListRes, curriculumListRes, allCategoriesRes]);

  const lessonFilterOptions = useMemo(() => {
    const lessons = Array.isArray(lessonListRes?.data) ? lessonListRes.data : [];
    const opts = lessons.map((l) => ({ value: l.id, label: lessonPathLabelMap[l.id] || l.title || '' }));
    opts.sort((a, b) => (a.label || '').localeCompare(b.label || ''));
    return [{ value: -1, label: 'Tất cả' }, ...opts];
  }, [lessonListRes, lessonPathLabelMap]);

  const userMap = useMemo(() => {
    const list = Array.isArray(usersRes?.data) ? usersRes.data : [];
    return list.reduce((acc, u) => { acc[u.id] = `${u.full_name || ''} (${u.phone || ''})`.trim(); return acc; }, {});
  }, [usersRes]);
  const quizMap = useMemo(() => {
    const list = Array.isArray(quizzesRes?.data) ? quizzesRes.data : [];
    return list.reduce((acc, q) => { acc[q.id] = q.title || `Đề #${q.id}`; return acc; }, {});
  }, [quizzesRes]);
  const quizLessonMap = useMemo(() => {
    const list = Array.isArray(quizzesRes?.data) ? quizzesRes.data : [];
    return list.reduce((acc, q) => { acc[q.id] = q.lesson_id || 0; return acc; }, {});
  }, [quizzesRes]);

  const handleSubmitForm = (values) => {
    const next = { page: 1, limit: params.limit };
    if (values?.user_id > 0) Object.assign(next, { user_id: values.user_id });
    if (values?.lesson_id > 0) Object.assign(next, { lesson_id: values.lesson_id });
    if (JSON.stringify(next) !== JSON.stringify(params)) { setParams(next); return; }
    refetch();
  };

  const columns = [
    { title: 'STT', dataIndex: 'id', key: 'stt', width: 60, fixed: 'left', align: 'center', ellipsis: true, onHeaderCell: () => ({ style: { maxWidth: 70 } }), onCell: () => ({ style: { maxWidth: 70, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' } }), render: (_, __, index) => (params?.page - 1) * params?.limit + index + 1 },
    { title: 'Học viên', dataIndex: 'user_id', key: 'user_id', render: (uid, record) => (
        <Button type="link" className="p-0" onClick={() => { setDetailRecord(record); setDetailOpen(true); }}>
          {userMap[uid] || `#${uid}`}
        </Button>
      ), minWidth: 140, width: 220 },
    { title: 'Bài học', dataIndex: 'quiz_id', key: 'lesson_display', render: (qid) => { const lid = quizLessonMap[qid] || 0; const label = lessonPathLabelMap[lid] || (lid ? `#${lid}` : '—'); return <span title={label}>{label}</span>; }, minWidth: 240, width: 340 },
    { title: 'Đề ôn tập', dataIndex: 'quiz_id', key: 'quiz_id', render: (qid) => <Text>{quizMap[qid] || `#${qid}`}</Text>, minWidth: 140, width: 240 },
    { title: 'Bắt đầu', dataIndex: 'start_time', key: 'start_time', render: (v) => v ? <Text className="text-[#667085]">{dayjs(v).format('DD/MM/YYYY HH:mm:ss')}</Text> : <Text>—</Text>, minWidth: 120, width: 120 },
    { title: 'Thời gian (phút)', dataIndex: 'end_time', key: 'duration', render: (_, r) => {
        const s = r?.start_time ? new Date(r.start_time) : null;
        const e = r?.end_time ? new Date(r.end_time) : null;
        if (!s || !e) return <Text>—</Text>;
        const ms = Math.max(0, e - s);
        const mins = Math.ceil(ms / 60000);
        return <Text>{`${mins}`}</Text>;
      }, width: 160 },
    { title: 'Điểm', dataIndex: 'score', key: 'score', render: (_, r) => {
        const sc = r?.score;
        if (typeof sc === 'number') return <Text>{Number.isInteger(sc) ? sc : sc.toFixed(2)}</Text>;
        const n = Number(sc);
        if (!Number.isNaN(n)) return <Text>{Number.isInteger(n) ? n : n.toFixed(2)}</Text>;
        return <Text>{r?.score || '—'}</Text>;
      }, width: 120 },
    { title: 'Số câu đúng', dataIndex: 'score_text', key: 'score_text', render: (v) => <Text>{v || '—'}</Text>, width: 120 },
  ];

  return (
    <div className="m-5">
      <div className="flex justify-between items-center mb-5">
        <h1 className="text-2xl">{itemName}</h1>
      </div>

      <Form name="search_quiz_attempts" form={formSearch} layout="vertical" requiredMark={false} className="mb-5" onFinish={handleSubmitForm}>
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} md={8} xl={6}>
            <Form.Item name="user_id" label={<Text>Học viên</Text>} initialValue={-1}>
              <Select
                showSearch
                allowClear
                placeholder="Chọn học viên"
                optionFilterProp="label"
                filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
                options={[{ value: -1, label: 'Tất cả' }, ...((Array.isArray(usersRes?.data) ? usersRes.data : []).map((u) => ({ value: u.id, label: `${u.full_name || ''} (${u.phone || ''})`.trim() })))]}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={24} md={18} xl={14}>
            <Form.Item name="lesson_id" label={<Text>Bài học</Text>} initialValue={-1}>
              <Select
                showSearch
                allowClear
                placeholder="Chọn bài học"
                optionFilterProp="label"
                filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
                options={lessonFilterOptions}
              />
            </Form.Item>
          </Col>
          <Col className="flex items-center gap-2">
            <Button type="primary" htmlType="submit"><SearchOutlined />Tìm kiếm</Button>
            {params && Object.values(newObjectKeys(params, ['user_id', 'lesson_id'])).some((value) => value) && (
              <Button htmlType="button" onClick={() => { formSearch.resetFields(); setParams({ page: 1, limit: params.limit }); }}>
                Xóa tìm kiếm
              </Button>
            )}
          </Col>
        </Row>
      </Form>

      <TableCustom columns={columns} dataSource={dataAttempts?.data ?? []} totalItem={dataAttempts?.total ?? 0} loading={isPending} params={params} setParams={setParams} />

      <Modal
        open={detailOpen}
        title={(() => {
          const ulabel = userMap[detailRecord?.user_id] || `#${detailRecord?.user_id || ''}`;
          const qlabel = quizMap[detailRecord?.quiz_id] || `Đề #${detailRecord?.quiz_id || ''}`;
          return `Bài làm: ${ulabel} - ${qlabel}`;
        })()}
        onCancel={() => { setDetailOpen(false); setDetailRecord(null); }}
        footer={null}
        width={800}
        destroyOnClose
      >
        {(() => {
          if (!detailRecord) return null;
          let parsed;
          try { parsed = JSON.parse(detailRecord.data || '{}'); } catch { parsed = {}; }
          const attemptList = Array.isArray(parsed?.questions) ? parsed.questions : [];
          // Combine with quiz detail to render full question + all answers in order
          const qFromApi = Array.isArray(quizDetail?.questions) ? quizDetail.questions : [];
          const list = qFromApi.length > 0 ? qFromApi.map((q) => {
            const matchedAttempt = attemptList.find((aq) => String(aq?.id) === String(q?.id));
            const userAnswer = matchedAttempt?.answer || {};
            const isCorrect = Number(matchedAttempt?.is_correct) === 1;
            return { ...q, _userAnswer: userAnswer, _userCorrect: isCorrect };
          }) : attemptList;
          const total = typeof parsed?.total === 'number' ? parsed.total : undefined;
          return (
            <div>
              <div className="text-sm text-[#667085] mb-2">
                Bắt đầu: {detailRecord?.start_time ? dayjs(detailRecord.start_time).format('DD/MM/YYYY HH:mm:ss') : '—'}
                {' · '}Thời gian làm bài: {detailRecord?.start_time && detailRecord?.end_time ? `${Math.ceil((new Date(detailRecord.end_time) - new Date(detailRecord.start_time)) / 60000)} phút` : '—'}
                {typeof detailRecord?.score !== 'undefined' && (
                  <>
                    {' · '}Điểm: {(() => { const sc = detailRecord?.score; if (typeof sc === 'number') return Number.isInteger(sc) ? sc : sc.toFixed(2); const n = Number(sc); return Number.isNaN(n) ? (detailRecord?.score || '—') : (Number.isInteger(n) ? n : n.toFixed(2)); })()}/10
                  </>
                )}
                {detailRecord?.score_text ? <> {' · '}Số câu đúng: {detailRecord.score_text}</> : null}
              </div>
              <Divider className="my-2" />
              <div className="max-h-[60vh] overflow-auto pr-2">
                {list.length === 0 ? (
                  <div className="text-[#667085]">Không có dữ liệu câu hỏi</div>
                ) : (
                  list.map((q, idx) => {
                    const qid = q?.id;
                    const qtype = Number(q?.question_type);
                    const typeLabel = qtype === 1 ? 'Chọn 1' : qtype === 2 ? 'Chọn nhiều' : 'Nhập đáp án';
                    const correct = q?._userCorrect === true || Number(q?._userCorrect) === 1 || Number(q?.is_correct) === 1;
                    const userAns = q?._userAnswer && typeof q._userAnswer === 'object' ? q._userAnswer : (q?.answer && typeof q.answer === 'object' ? q.answer : {});
                    const uaEntries = Object.entries(userAns || {});
                    const noAnswer = uaEntries.length === 0 || uaEntries.every(([, v]) => {
                      if (v === undefined || v === null) return true;
                      if (typeof v === 'string') return v.trim() === '';
                      if (typeof v === 'object') return Object.keys(v).length === 0;
                      return false;
                    });

                    return (
                      <div key={`${qid}-${idx}`} className="mb-4">
                        <div className="font-medium">Câu {idx + 1} {noAnswer ? <Tag color="red">Không làm</Tag> : (correct ? <Tag color="green">Đúng</Tag> : <Tag color="red">Sai</Tag>)}</div>
                        <div className="text-sm text-[#344054] mb-1">Điểm: {(() => { const p = q?.point; if (typeof p === 'number') return Number.isInteger(p) ? p : p.toFixed(2); const n = Number(p); return Number.isNaN(n) ? '—' : (Number.isInteger(n) ? n : n.toFixed(2)); })()}</div>
                        {/* Content (if available) */}
                        {q?.content ? <div className="whitespace-pre-wrap mb-2">{q.content}</div> : null}
                        {/* Show image if available */}
                        {q?.content_image ? (
                          <div className="mb-2"><img src={q.content_image} alt={`q-${qid}`} style={{ maxWidth: '100%', maxHeight: 200, borderRadius: 6 }} /></div>
                        ) : null}

                        {/* Answers section - render by question type */}
                        {(() => {
                          const answers = Array.isArray(q?.answers) ? q.answers : [];
                          if (qtype === 1) {
                            // Single choice: RadioGroup
                            const selectedId = (() => {
                              const keys = Object.keys(userAns || {});
                              return keys.length > 0 ? String(keys[0]) : undefined;
                            })();
                            return (
                              <div className="mt-1">
                                <div className="text-xs text-gray-500 mb-1">Các đáp án:</div>
                                <Radio.Group value={selectedId}>
                                  <Space direction="vertical">
                                    {answers.map((a, i) => {
                                      const aid = String(a?.id ?? a?.label ?? i);
                                      const isCorrectA = Number(a?.is_correct) === 1 || a?.is_correct === true;
                                      const content = a?.content || '';
                                      return (
                                        <Radio key={`${qid}-radio-${aid}`} value={aid} disabled>
                                          <span className={`${isCorrectA ? 'text-green-600' : ''}`}>
                                            {a?.label ? `${String(a.label).toUpperCase()}. ` : ''}{content || '(trống)'}
                                          </span>
                                        </Radio>
                                      );
                                    })}
                                  </Space>
                                </Radio.Group>
                              </div>
                            );
                          }
                          if (qtype === 2) {
                            // Multiple choice: CheckboxGroup
                            const selectedIds = Object.keys(userAns || {}).map((k) => String(k));
                            return (
                              <div className="mt-1">
                                <div className="text-xs text-gray-500 mb-1">Các đáp án:</div>
                                <Checkbox.Group value={selectedIds}>
                                  <Space direction="vertical">
                                    {answers.map((a, i) => {
                                      const aid = String(a?.id ?? a?.label ?? i);
                                      const isCorrectA = Number(a?.is_correct) === 1 || a?.is_correct === true;
                                      const content = a?.content || '';
                                      return (
                                        <Checkbox key={`${qid}-check-${aid}`} value={aid} disabled>
                                          <span className={`${isCorrectA ? 'text-green-600' : ''}`}>
                                            {a?.label ? `${String(a.label).toUpperCase()}. ` : ''}{content || '(trống)'}
                                          </span>
                                        </Checkbox>
                                      );
                                    })}
                                  </Space>
                                </Checkbox.Group>
                              </div>
                            );
                          }
                          // Input type (qtype === 3): show inputs with correct answer next to it
                          return (
                            <div className="mt-1">
                              <div className="text-xs text-gray-500 mb-2">Câu trả lời đã nhập:</div>
                              <Space direction="vertical" style={{ width: '100%' }}>
                                {answers.length > 0 ? (
                                  answers.map((a, i) => {
                                    const aid = String(a?.id ?? a?.label ?? i);
                                    let title = '';
                                    let correctAns = '';
                                    try {
                                      const obj = JSON.parse(a?.content || '{}');
                                      title = obj?.title || '';
                                      correctAns = obj?.answer || '';
                                    } catch {}
                                    const userVal = userAns ? (userAns[aid] ?? '') : '';
                                    return (
                                      <div key={`${qid}-inputrow-${aid}`} className="flex items-center gap-2">
                                        <div className="text-sm text-[#344054]" style={{ minWidth: 140 }}>
                                          {title || `Trường ${i + 1}`}
                                        </div>
                                        <div>
                                          <Input size="small" style={{ maxWidth: 260 }} value={String(userVal || '')} disabled />
                                        </div>
                                        <div className="text-green-600 text-sm whitespace-pre">{correctAns ? `Đúng: ${correctAns}` : ''}</div>
                                      </div>
                                    );
                                  })
                                ) : (
                                  // fallback if answers array empty: show raw user inputs
                                  (() => {
                                    const entries = Object.entries(userAns || {});
                                    if (entries.length === 0) return <div className="text-[#667085]">—</div>;
                                    return entries.map(([k, v]) => (
                                      <div key={`${qid}-inputraw-${k}`} className="flex items-center gap-2">
                                        <div style={{ flex: 1 }}>
                                          <Input value={String(v || '')} disabled />
                                        </div>
                                      </div>
                                    ));
                                  })()
                                )}
                              </Space>
                            </div>
                          );
                        })()}
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          );
        })()}
      </Modal>
    </div>
  );
};

export default QuizAttemptsList;
